import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

import type { EsnSegmentedOption } from '../../../shared/types/forms';
import { IconComponent } from '../../atoms/icon/icon.component';

/**
 * A row of mutually exclusive choices (e.g. the feedback kind): one radiogroup, the selected
 * segment in Space Violet, arrow keys move the choice. Works with `[(value)]` and forms.
 */
@Component({
  selector: 'esn-segmented',
  imports: [IconComponent],
  templateUrl: './segmented.component.html',
  styleUrl: './segmented.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SegmentedComponent), multi: true }],
})
export class SegmentedComponent implements ControlValueAccessor {
  readonly value = model<string | null>(null);
  readonly options = input<EsnSegmentedOption[]>([]);
  readonly ariaLabel = input<string | null>(null);
  readonly disabled = input(false);

  private readonly formDisabled = signal(false);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

  /**
   * The one segment reachable by Tab: the selected one, else the first enabled one.
   */
  protected readonly tabStop = computed(() => {
    const options = this.options();
    const selected = options.findIndex((o) => o.value === this.value() && !o.disabled);
    return selected >= 0 ? selected : options.findIndex((o) => !o.disabled);
  });

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected select(index: number): void {
    const option = this.options()[index];
    if (!option || option.disabled || this.isDisabled()) return;
    if (option.value !== this.value()) {
      this.value.set(option.value);
      this.onChange(option.value);
    }
  }

  protected handleKeydown(event: KeyboardEvent, index: number): void {
    const count = this.options().length;
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = this.nextEnabled(index, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = this.nextEnabled(index, -1);
        break;
      case 'Home':
        next = this.nextEnabled(count - 1, 1);
        break;
      case 'End':
        next = this.nextEnabled(0, -1);
        break;
      default:
        return;
    }
    event.preventDefault();
    this.select(next);
    this.host.nativeElement.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.formDisabled.set(disabled);
  }

  /**
   * Next enabled segment after `from` in direction `step`, wrapping around the ends.
   */
  private nextEnabled(from: number, step: 1 | -1): number {
    const options = this.options();
    const count = options.length;
    for (let n = 1; n <= count; n++) {
      const i = (((from + step * n) % count) + count) % count;
      if (!options[i].disabled) return i;
    }
    return from;
  }
}
